import { DirectionalHint, IDropdownOption, Stack, TooltipHost } from '@fluentui/react';
import {
  FormHookDropdown,
  FormHookTextField,
} from 'common/components/FormHooksFields';
import React from 'react';
import { useQuery } from '@apollo/client';
import { FormHookCheckBox } from 'common/components/FormHooksFields/FormHookCheckBox';
import { UserTemplateCommonData } from './__generated__/UserTemplateCommonData';
import COMMON_DATA from './UserTemplateCommonData.graphql';

const TOOLTIP_TEXT_DEFAULT_SETTINGS = "When entered, the values will be included in user's preference"
const TOOLTIP_TEXT_DEFAULT_CURRENCY =
  "When checked, default currency will be included in user's preference.";
const TOOLTIP_TEXT_DEFAULT_DEPARTMENT =
  "When checked, user's department will be included in user's preference.";
const TOOLTIP_TEXT_DEFAULT_BUSINESS_UNIT =
  "When checked, default business unit will be included in user's preference.";
const TOOLTIP_TEXT_TEMPLATE_CONTROLLED =
  "When checked, default values entered above cannot be altered at the user level.";
const TOOLTIP_TEXT_USER_GROUP_REQUIRED =
  "When checked, the user must belong to a user group.";

interface BasicFormProps {
  isUpdatable?: boolean;
}
export const BasicForm: React.FC<BasicFormProps> = ({ isUpdatable }) => {
  const { data: commonData } = useQuery<UserTemplateCommonData>(COMMON_DATA);
  const roleOptions: IDropdownOption[] =
    commonData?.roles?.nodes.map((ele) => ({
      key: ele.id,
      text: ele.name,
    })) || [];
  const occupationOptions: IDropdownOption[] =
    commonData?.userOccupationTitles?.nodes.map((ele) => ({
      key: ele.id,
      text: ele.userOccupationTitle,
    })) || [];
  const departmentOptions: IDropdownOption[] =
    commonData?.companyDepartments?.nodes.map((ele) => ({
      key: ele.id || '',
      text: ele.name || '',
    })) || [];
  const departmentGroupOptions: IDropdownOption[] =
    commonData?.departmentGroups?.nodes.map((ele) => ({
      key: ele.id || '',
      text: ele.name || '',
    })) || [];
  const accessPolicyOptions: IDropdownOption[] =
    commonData?.dataAccessPolicies?.nodes.map((ele) => ({
      key: ele.id || '',
      text: ele.name || '',
    })) || [];
  const userGroupOptions: IDropdownOption[] =
    commonData?.userGroups?.nodes.map((ele) => ({
      key: ele.id || '',
      text: ele.name || '',
    })) || [];
  const userGroupAccessOptions: IDropdownOption[] =
    commonData?.accessGroups?.nodes.map((ele) => ({
      key: ele.id || '',
      text: ele.name || '',
    })) || [];
  const tagGroupOptions: IDropdownOption[] =
    commonData?.tagGroups?.nodes.map((ele) => ({
      key: ele.id || '',
      text: ele.name || '',
    })) || [];
  const communicationGroupOptions: IDropdownOption[] =
    commonData?.communicationGroups?.nodes.map((ele) => ({
      key: ele.id || '',
      text: ele.name || '',
    })) || [];
  const rankOptions: IDropdownOption[] =
    commonData?.userRankTypes?.nodes.map((ele) => ({
      key: ele.id || '',
      text: ele.userRank || '',
    })) || [];
  const securityLevelOptions: IDropdownOption[] =
    commonData?.secureRowLevels?.nodes.map((ele) => ({
      key: ele.id || '',
      text: ele.name || '',
    })) || [];

  return (
    <Stack
      tokens={{
        padding: 30,
        childrenGap: 10,
      }}
    >
      <Stack
        horizontal
        tokens={{
          childrenGap: 20,
        }}
      >
        <Stack style={{ flex: 1 }}>
          <FormHookTextField
            name="name"
            label="Name"
            required
            disabled={!isUpdatable}
          />
        </Stack>
        <Stack style={{ flex: 1 }}>
          <FormHookTextField
            name="description"
            label="Description"
            disabled={!isUpdatable}
          />
        </Stack>
      </Stack>
      <Stack
        horizontal
        style={{ flex: 1 }}
        tokens={{
          childrenGap: 20,
        }}
      >
        <Stack style={{ flex: 1 }}>
          <FormHookDropdown
            label="Role"
            name="roleId"
            options={roleOptions}
            disabled={!isUpdatable}
            required
          />
        </Stack>
        <Stack style={{ flex: 1 }}>
          <FormHookDropdown
            label="Occupation"
            name="userOccupationTitleId"
            options={occupationOptions}
            disabled={!isUpdatable}
          />
        </Stack>
      </Stack>
      <Stack
        horizontal
        style={{ flex: 1 }}
        tokens={{
          childrenGap: 20,
        }}
      >
        <Stack style={{ flex: 1 }}>
          <FormHookDropdown
            label="Department"
            name="departmentId"
            options={departmentOptions}
            disabled={!isUpdatable}
          />
        </Stack>
        <Stack style={{ flex: 1 }}>
          <FormHookDropdown
            label="Department Group"
            name="departmentGroupId"
            options={departmentGroupOptions}
            disabled={!isUpdatable}
          />
        </Stack>
      </Stack>
      <Stack
        horizontal
        style={{ flex: 1 }}
        tokens={{
          childrenGap: 20,
        }}
      >
        <Stack style={{ flex: 1 }}>
          <FormHookDropdown
            label="Access Policy"
            name="dataAccessPolicyId"
            options={accessPolicyOptions}
            disabled={!isUpdatable}
            required
          />
        </Stack>
        <Stack style={{ flex: 1 }}>
          <FormHookDropdown
            label="User Group"
            name="userGroupId"
            options={userGroupOptions}
            disabled={!isUpdatable}
          />
        </Stack>
      </Stack>
      <Stack
        horizontal
        style={{ flex: 1 }}
        tokens={{
          childrenGap: 20,
        }}
      >
        <Stack style={{ flex: 1 }}>
          <FormHookDropdown
            label="User Access Pool"
            name="accessGroupId"
            options={userGroupAccessOptions}
            disabled={!isUpdatable}
          />
        </Stack>
        <Stack style={{ flex: 1 }}>
          <FormHookDropdown
            label="Tagging Group"
            name="tagGroupId"
            options={tagGroupOptions}
            disabled={!isUpdatable}
          />
        </Stack>
      </Stack>
      <Stack
        horizontal
        style={{ flex: 1 }}
        tokens={{
          childrenGap: 20,
        }}
      >
        <Stack style={{ flex: 1 }}>
          <FormHookDropdown
            label="Communication Group"
            name="communicationGroupId"
            options={communicationGroupOptions}
            disabled={!isUpdatable}
          />
        </Stack>
        <Stack style={{ flex: 1 }}>
          <FormHookDropdown
            label="Rank"
            name="userRankTypeId"
            options={rankOptions}
            disabled={!isUpdatable}
          />
        </Stack>
      </Stack>
      <Stack
        horizontal
        style={{ flex: 1 }}
        tokens={{
          childrenGap: 20,
        }}
      >
        <Stack style={{ flex: 0.5 }}>
          <FormHookDropdown
            label="Security Level"
            name="secureRowLevelId"
            options={securityLevelOptions}
            disabled={!isUpdatable}
          />
        </Stack>
        <Stack
          style={{ flex: 0.5 }}
          horizontal
          tokens={{
            childrenGap: 40,
          }}
        >
          <TooltipHost content={TOOLTIP_TEXT_DEFAULT_SETTINGS} directionalHint={DirectionalHint.topAutoEdge}>
            <FormHookTextField
              name="userDefaultCompany"
              label="Default Company"
              disabled={!isUpdatable}
            />
          </TooltipHost>
          <TooltipHost content={TOOLTIP_TEXT_DEFAULT_SETTINGS} directionalHint={DirectionalHint.topAutoEdge}>
            <FormHookTextField
              name="userDefaultLocation"
              label="Default Location"
              disabled={!isUpdatable}
            />
          </TooltipHost>
        </Stack>
      </Stack>
      <Stack
        horizontal
        style={{ flex: 1 }}
        tokens={{
          childrenGap: 30,
          padding: '20px 0px 0px 0px',
        }}
      >
        <TooltipHost content={TOOLTIP_TEXT_DEFAULT_CURRENCY}>
          <FormHookCheckBox
            name="isUserPrimaryCurrencyDefault"
            label="Add Default Currency"
            disabled={!isUpdatable}
          />
        </TooltipHost>

        <TooltipHost content={TOOLTIP_TEXT_DEFAULT_DEPARTMENT}>
          <FormHookCheckBox
            name="isUserDepartmentDefault"
            label="Add Default Department"
            disabled={!isUpdatable}
          />
        </TooltipHost>

        <TooltipHost content={TOOLTIP_TEXT_DEFAULT_BUSINESS_UNIT}>
          <FormHookCheckBox
            name="isUserPrimaryBusinessUnitDefault"
            label="Add Default Business Unit"
            disabled={!isUpdatable}
          />
        </TooltipHost>

        <TooltipHost content={TOOLTIP_TEXT_TEMPLATE_CONTROLLED}>
          <FormHookCheckBox
            name="isTemplateControlled"
            label="Template Controlled"
            disabled={!isUpdatable}
          />
        </TooltipHost>

        <TooltipHost content={TOOLTIP_TEXT_USER_GROUP_REQUIRED}>
          <FormHookCheckBox
            name="isDepartmentUserGroupRequired"
            label="User Group Required"
            disabled={!isUpdatable}
          />
        </TooltipHost>
      </Stack>
    </Stack>
  );
};
